import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Phone, Mailbox, ExternalLink, Smartphone, Shield } from 'lucide-react';
export function SpamGuidePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen w-full bg-white font-sans text-[#1a0647]" data-id="element-4161">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-4162">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-4163">
          <div className="flex items-center gap-5" data-id="element-4164">
            <Link to="/home" data-id="element-4165">
              <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo" className="h-7 w-auto" data-id="element-4166" />
            </Link>
            <Link to="/resources" className="text-gray-400 hover:text-gray-600 text-sm transition-colors hidden sm:inline-flex items-center gap-1" data-id="element-4167">
              ← Back to Resources
            </Link>
          </div>

          <div className="flex items-center gap-3" data-id="element-4168">
            <a href="/login" className="text-[#111111] no-underline hover:text-[#631bb7] px-3 py-1.5 rounded-lg font-medium text-sm transition-colors" data-id="element-4169">
              Login
            </a>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-4 py-2 rounded-lg font-semibold text-sm no-underline hover:bg-[#521099] transition-colors shadow-sm" data-id="element-4170">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#1a0647] text-white py-10 md:py-12 px-4" data-id="element-4171">
        <div className="max-w-5xl mx-auto" data-id="element-4172">
          <div className="flex flex-row items-center gap-4" data-id="element-4173">
            <div className="flex-1" data-id="element-4174">
              <h1 className="font-bold text-2xl md:text-3xl lg:text-4xl leading-tight text-white" data-id="element-4175">
                Reducing Spam and Junk Mail
              </h1>
              <p className="text-gray-300 mt-3 text-base md:text-lg max-w-2xl" data-id="element-4176">
                Spam doesn't stop on its own. Here's how to push back across
                every channel, using tools you already have on your phone or
                computer.
              </p>
            </div>
            <div className="flex-shrink-0 w-20 md:w-28" data-id="element-4177">
              <div className="w-full aspect-square bg-[#8528e2]/20 rounded-2xl flex items-center justify-center" data-id="element-4178">
                <Shield size={48} className="text-[#b063ff]" data-id="element-4179" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-[#f8f8f8] py-6 px-4 border-b border-gray-200" data-id="element-4180">
        <div className="max-w-5xl mx-auto" data-id="element-4181">
          <div className="flex flex-wrap justify-center gap-3" data-id="element-4182">
            <a href="#email" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-4183">
              <Mail size={16} data-id="element-4184" />
              Email
            </a>
            <a href="#text" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-4185">
              <MessageSquare size={16} data-id="element-4186" />
              Text Messages
            </a>
            <a href="#phone" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-4187">
              <Phone size={16} data-id="element-4188" />
              Phone Calls
            </a>
            <a href="#mail" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-4189">
              <Mailbox size={16} data-id="element-4190" />
              Physical Mail
            </a>
          </div>
        </div>
      </section>

      {/* EMAIL Section */}
      <section id="email" className="py-16 md:py-20 px-4 max-w-5xl mx-auto" data-id="element-4191">
        <div className="flex items-center gap-4 mb-8" data-id="element-4192">
          <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-4193">
            <Mail size={28} className="text-white" data-id="element-4194" />
          </div>
          <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-4195">
            Email
          </h2>
        </div>

        <p className="text-lg text-gray-700 leading-relaxed mb-10 max-w-3xl" data-id="element-4196">
          Your email provider has built-in tools to report suspicious messages,
          and using them actually makes a difference. Every report you submit
          helps train the filter to catch more junk in the future.
        </p>

        <div className="space-y-6" data-id="element-4197">
          {/* Gmail Desktop/Android */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-4198">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4199">
              Gmail (on a computer or Android)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-4200">
              <li data-id="element-4201">Open the suspicious email.</li>
              <li data-id="element-4202">Click the three vertical dots next to the Reply button.</li>
              <li data-id="element-4203">
                Select <strong data-id="element-4204">"Report phishing"</strong> if it looks like a
                scam trying to steal your information, or{' '}
                <strong data-id="element-4205">"Report spam"</strong> if it's just junk you didn't ask
                for.
              </li>
              <li data-id="element-4206">
                The email disappears from your inbox and gets flagged for
                review.
              </li>
            </ol>
          </div>

          {/* Gmail iPhone */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-4207">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4208">
              Gmail (on iPhone)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-4209">
              <li data-id="element-4210">Open the email.</li>
              <li data-id="element-4211">Tap the three dots at the top right of the message.</li>
              <li data-id="element-4212">
                Tap <strong data-id="element-4213">"Report spam."</strong>
              </li>
            </ol>
            <p className="text-sm text-gray-500 mt-3 italic" data-id="element-4214">
              There is no separate "Report phishing" option in the iPhone app,
              so this is the right button for both situations.
            </p>
          </div>

          {/* Outlook */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-4215">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4216">
              Outlook (on a computer)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-4217">
              <li data-id="element-4218">
                Select the suspicious message and click the{' '}
                <strong data-id="element-4219">"Report"</strong> button in the toolbar at the top of
                the screen.
              </li>
              <li data-id="element-4220">
                Choose <strong data-id="element-4221">"Report phishing"</strong> or{' '}
                <strong data-id="element-4222">"Report junk."</strong>
              </li>
            </ol>
            <p className="text-sm text-gray-500 mt-3" data-id="element-4223">
              Microsoft uses your reports to improve filtering for everyone.
            </p>
          </div>

          {/* Apple Mail */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-4224">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4225">
              Apple Mail (on a Mac or iPhone)
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-4226">
              Apple Mail does not have a dedicated "Report phishing" button.
              Your best option is to right-click the email and select{' '}
              <strong data-id="element-4227">"Move to Junk,"</strong> which tells Apple's filters to
              treat that sender as unwanted going forward.
            </p>
            <p className="text-gray-700" data-id="element-4228">
              For anything that looks like a real scam, forward it to{' '}
              <a href="mailto:reportphishing@apple.com" className="text-[#8528e2] font-medium hover:underline" data-id="element-4229">
                reportphishing@apple.com
              </a>
              .
            </p>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="mt-10 space-y-6" data-id="element-4230">
          <div className="bg-[#eae6dc] border-t-4 border-[#8528e2] p-6 rounded-lg" data-id="element-4231">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4232">
              💡 Want to fight back more actively?
            </h3>
            <p className="text-gray-700" data-id="element-4233">
              Forward scam emails to Re:Scam at{' '}
              <a href="mailto:me@rescam.org" className="text-[#8528e2] font-medium hover:underline" data-id="element-4234">
                me@rescam.org
              </a>
              . Their AI will reply to the scammer on your behalf and keep them
              tied up in a fake conversation, wasting their time so they have
              less of it to spend on real targets.
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-4235">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4236">
              Opt out of marketing email lists
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-4237">
              You can also opt out of marketing email lists at:
            </p>
            <a href="https://www.ims-dm.com/cgi/offemaillist.php" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#8528e2] font-medium hover:underline" data-id="element-4238">
              ims-dm.com/cgi/offemaillist.php
              <ExternalLink size={14} data-id="element-4239" />
            </a>
          </div>
        </div>
      </section>

      {/* TEXT MESSAGES Section */}
      <section id="text" className="py-16 md:py-20 px-4 bg-[#f8f8f8] border-t border-gray-200" data-id="element-4240">
        <div className="max-w-5xl mx-auto" data-id="element-4241">
          <div className="flex items-center gap-4 mb-8" data-id="element-4242">
            <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-4243">
              <MessageSquare size={28} className="text-white" data-id="element-4244" />
            </div>
            <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-4245">
              Text Messages
            </h2>
          </div>

          <div className="space-y-8" data-id="element-4246">
            {/* Report to Carrier */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4247">
              <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-4248">
                Report to your carrier
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-4249">
                The fastest way to report a spam text is to forward it to{' '}
                <strong className="text-[#8528e2]" data-id="element-4250">7726</strong>. That number
                spells "SPAM" on your keypad. All three major carriers (AT&T,
                T-Mobile, and Verizon) use this number.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-4251">
                After you forward the message, you'll get a reply asking for the
                sender's phone number. Just reply with the number that texted
                you.
              </p>
              <div className="bg-[#eae6dc] p-4 rounded-lg" data-id="element-4252">
                <p className="text-[#1a0647] font-medium" data-id="element-4253">
                  <strong data-id="element-4254">Pro tip:</strong> Save 7726 in your contacts as
                  "Report SPAM" so you can find it quickly when you need it.
                </p>
              </div>
            </div>

            {/* iPhone */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4255">
              <div className="flex items-center gap-3 mb-4" data-id="element-4256">
                <Smartphone size={24} className="text-[#8528e2]" data-id="element-4257" />
                <h3 className="font-bold text-xl text-[#1a0647]" data-id="element-4258">
                  iPhone users: built-in reporting
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-4259">
                If you receive a text from a number that is not in your
                contacts, a <strong data-id="element-4260">"Report Junk"</strong> link will appear at
                the bottom of the message. Tap it, then tap{' '}
                <strong data-id="element-4261">"Delete and Report Junk."</strong> This reports the
                sender to Apple and your carrier at the same time.
              </p>
              <p className="text-sm text-gray-500 italic mb-6" data-id="element-4262">
                Note: if you have already replied to the message, this option
                will no longer appear.
              </p>

              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-4263">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4264">
                  Filter unknown senders
                </h4>
                <p className="text-gray-700 text-sm mb-3" data-id="element-4265">
                  You can turn on filtering so texts from unknown numbers are
                  silently sorted into a separate folder:
                </p>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-4266">
                  <li data-id="element-4267">
                    Go to <strong data-id="element-4268">Settings</strong>
                  </li>
                  <li data-id="element-4269">
                    Tap <strong data-id="element-4270">Apps</strong>, then tap{' '}
                    <strong data-id="element-4271">Messages</strong>
                  </li>
                  <li data-id="element-4272">
                    Turn on <strong data-id="element-4273">"Filter Unknown Senders"</strong>
                  </li>
                </ol>
                <p className="text-gray-600 text-sm mt-3" data-id="element-4274">
                  Texts from numbers you don't know will land in a separate
                  folder labeled "Unknown Senders," and you won't get a
                  notification.
                </p>
              </div>
            </div>

            {/* Android */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4275">
              <div className="flex items-center gap-3 mb-4" data-id="element-4276">
                <Smartphone size={24} className="text-[#8528e2]" data-id="element-4277" />
                <h3 className="font-bold text-xl text-[#1a0647]" data-id="element-4278">
                  Android users: built-in reporting and filtering
                </h3>
              </div>

              <p className="text-gray-700 mb-4" data-id="element-4279">
                If you use the Google Messages app (the default on most Android
                phones), spam protection is usually on by default. To confirm
                it's active:
              </p>
              <ol className="list-decimal pl-5 text-gray-700 mb-6 space-y-1" data-id="element-4280">
                <li data-id="element-4281">Tap your profile picture or initials in the top right</li>
                <li data-id="element-4282">
                  Go to <strong data-id="element-4283">"Messages settings"</strong>
                </li>
                <li data-id="element-4284">
                  Then <strong data-id="element-4285">"Spam protection"</strong>
                </li>
                <li data-id="element-4286">
                  Make sure <strong data-id="element-4287">"Enable spam protection"</strong> is toggled
                  on
                </li>
              </ol>

              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg mb-4" data-id="element-4288">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4289">
                  To report a spam text:
                </h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-4290">
                  <li data-id="element-4291">Press and hold the conversation in your message list</li>
                  <li data-id="element-4292">
                    Tap <strong data-id="element-4293">"Block"</strong>
                  </li>
                  <li data-id="element-4294">
                    Tap <strong data-id="element-4295">"Report spam"</strong>
                  </li>
                  <li data-id="element-4296">
                    Tap <strong data-id="element-4297">"OK"</strong>
                  </li>
                </ol>
                <p className="text-gray-600 text-sm mt-2" data-id="element-4298">
                  This blocks the sender, moves the message to your spam folder,
                  and reports the number to Google.
                </p>
              </div>

              <p className="text-gray-700 text-sm" data-id="element-4299">
                <strong data-id="element-4300">Samsung phone?</strong> Open the Samsung Messages app,
                tap the three dots, go to "Settings," then "Block numbers and
                spam," and toggle on "Filter spam messages."
              </p>
            </div>

            {/* FTC */}
            <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-4301">
              <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-4302">
                Report to the FTC
              </h3>
              <p className="text-gray-700 mb-3" data-id="element-4303">
                You can also report spam texts (and scam calls) at{' '}
                <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer" className="text-[#8528e2] font-medium hover:underline" data-id="element-4304">
                  ReportFraud.ftc.gov
                </a>
                . The FTC uses these reports to investigate the biggest
                offenders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHONE CALLS Section */}
      <section id="phone" className="py-16 md:py-20 px-4 max-w-5xl mx-auto" data-id="element-4305">
        <div className="flex items-center gap-4 mb-8" data-id="element-4306">
          <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-4307">
            <Phone size={28} className="text-white" data-id="element-4308" />
          </div>
          <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-4309">
            Phone Calls
          </h2>
        </div>

        <div className="space-y-8" data-id="element-4310">
          {/* Do Not Call Registry */}
          <div className="bg-[#eae6dc] p-6 rounded-xl" data-id="element-4311">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-4312">
              Register with the Do Not Call Registry
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-4313">
              Start here if you haven't already. Visit{' '}
              <a href="https://www.donotcall.gov" target="_blank" rel="noopener noreferrer" className="text-[#8528e2] font-medium hover:underline" data-id="element-4314">
                DoNotCall.gov
              </a>{' '}
              or call <strong className="text-[#1a0647]" data-id="element-4315">1-888-382-1222</strong>{' '}
              (TTY: 1-866-290-4236) from the phone number you want to register.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-4316">
              If you register online, you'll receive a confirmation email with a
              link you'll need to click within 72 hours to complete your
              registration. You must call from, or register, the exact number
              you want protected.
            </p>
            <p className="text-gray-600 text-sm italic" data-id="element-4317">
              Legitimate telemarketers are required to honor this list. Scammers
              won't, which is why the tools below matter just as much.
            </p>
          </div>

          {/* Carrier Tools */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4318">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-4319">
              Turn on your carrier's free spam blocking tool
            </h3>
            <p className="text-gray-700 mb-6" data-id="element-4320">
              Your phone company almost certainly offers a free app or service
              that blocks scam calls before they even ring. Here's what each
              major carrier offers:
            </p>

            <div className="space-y-4" data-id="element-4321">
              {/* T-Mobile */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#e20074] p-5 rounded-r-lg" data-id="element-4322">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4323">
                  T-Mobile (including Metro by T-Mobile)
                </h4>
                <p className="text-gray-700 text-sm mb-2" data-id="element-4324">
                  <strong data-id="element-4325">Scam ID</strong> is already enabled on your account by
                  default. When a suspicious call comes in, it will show "Scam
                  Likely" on your screen so you know not to answer.
                </p>
                <p className="text-gray-700 text-sm mb-2" data-id="element-4326">
                  To take it a step further, you can turn on{' '}
                  <strong data-id="element-4327">Scam Block</strong>, which automatically prevents
                  those calls from going through at all.
                </p>
                <p className="text-[#1a0647] font-medium text-sm" data-id="element-4328">
                  Dial <strong data-id="element-4329">#662#</strong> from your T-Mobile phone to turn
                  it on. You can also download the T-Life app and manage it from
                  there.
                </p>
              </div>

              {/* AT&T */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#009fdb] p-5 rounded-r-lg" data-id="element-4330">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4331">AT&T</h4>
                <p className="text-gray-700 text-sm" data-id="element-4332">
                  Download the free <strong data-id="element-4333">AT&T ActiveArmor app</strong> from
                  the App Store or Google Play. Once installed, it automatically
                  blocks fraud calls at the network level, flags suspected spam,
                  and lets you send unknown callers straight to voicemail. No
                  ongoing setup required after the initial download.
                </p>
              </div>

              {/* Verizon */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#cd040b] p-5 rounded-r-lg" data-id="element-4334">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4335">Verizon</h4>
                <p className="text-gray-700 text-sm" data-id="element-4336">
                  Download the free <strong data-id="element-4337">Verizon Call Filter app</strong>{' '}
                  from the App Store or Google Play. It identifies incoming spam
                  calls, alerts you when one is suspected, and lets you report
                  numbers directly from the app. A paid upgrade (Call Filter
                  Plus) adds caller ID and additional features, but the free
                  version is a solid starting point.
                </p>
              </div>
            </div>
          </div>

          {/* Block Numbers */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4338">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-4339">
              How to block numbers on your phone
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-4340">
              If you receive a specific number you want to block permanently,
              here's how to do it by device:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4341">
              <div className="bg-[#f8f8f8] p-4 rounded-lg" data-id="element-4342">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4343">On iPhone</h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-4344">
                  <li data-id="element-4345">Open the Phone app</li>
                  <li data-id="element-4346">Tap "Recents"</li>
                  <li data-id="element-4347">Find the number</li>
                  <li data-id="element-4348">Tap the small "i" icon next to it</li>
                  <li data-id="element-4349">Scroll down</li>
                  <li data-id="element-4350">
                    Tap <strong data-id="element-4351">"Block this Caller"</strong>
                  </li>
                </ol>
              </div>

              <div className="bg-[#f8f8f8] p-4 rounded-lg" data-id="element-4352">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-4353">
                  On Android (Google Phone app)
                </h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-4354">
                  <li data-id="element-4355">Open the Phone app</li>
                  <li data-id="element-4356">Tap "Recents"</li>
                  <li data-id="element-4357">Tap the number</li>
                  <li data-id="element-4358">
                    Tap <strong data-id="element-4359">"Block/report spam"</strong>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHYSICAL MAIL Section */}
      <section id="mail" className="py-16 md:py-20 px-4 bg-[#f8f8f8] border-t border-gray-200" data-id="element-4360">
        <div className="max-w-5xl mx-auto" data-id="element-4361">
          <div className="flex items-center gap-4 mb-8" data-id="element-4362">
            <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-4363">
              <Mailbox size={28} className="text-white" data-id="element-4364" />
            </div>
            <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-4365">
              Physical Mail
            </h2>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm max-w-3xl" data-id="element-4366">
            <p className="text-gray-700 text-lg mb-6" data-id="element-4367">
              To reduce unsolicited catalogs, credit card offers, and marketing
              mailers, register with <strong data-id="element-4368">DMAchoice</strong>, a mail
              preference service run by the Data and Marketing Association.
            </p>
            <p className="text-gray-700 mb-6" data-id="element-4369">
              You can manage your preferences at:
            </p>
            <a href="https://www.dmachoice.org/static/consumer_choice_tools.php" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#8528e2] hover:bg-[#b063ff] text-white px-6 py-3 rounded-lg font-bold transition-colors" data-id="element-4370">
              Visit DMAchoice
              <ExternalLink size={16} data-id="element-4371" />
            </a>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="bg-[#1a0647] text-white py-16 md:py-20 px-4" data-id="element-4372">
        <div className="max-w-4xl mx-auto text-center" data-id="element-4373">
          <div className="flex justify-center mb-8" data-id="element-4374">
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Icon-Solo-FC.png" alt="Caveo Icon" className="h-16 w-auto" data-id="element-4375" />
          </div>
          <h2 className="font-bold text-3xl mb-6" data-id="element-4376">A Note from Caveo</h2>
          <div className="text-lg text-gray-300 leading-relaxed space-y-6 mb-10 max-w-3xl mx-auto" data-id="element-4377">
            <p data-id="element-4378">
              Spam is more than annoying — it's often the first step in a scam.
              By taking a few minutes to report, block, and opt out, you're not
              just cleaning up your inbox. You're making it harder for bad
              actors to reach you and your family.
            </p>
            <p data-id="element-4379">
              Every report you submit helps train filters to catch more junk.
              Every number you block is one less interruption. Small actions add
              up.
            </p>
          </div>
          <Link to="/intro-assessment" className="inline-block bg-[#8528e2] hover:bg-[#b063ff] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg no-underline" data-id="element-4380">
            Get Your Free Security Checkup
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#110430] text-gray-400 py-12 px-4 border-t border-white/10" data-id="element-4381">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left" data-id="element-4382">
          <div className="flex items-center gap-4" data-id="element-4383">
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Icon-Solo-FC.png" alt="Caveo" className="h-8 w-auto opacity-80" data-id="element-4384" />
            <span className="text-xs" data-id="element-4385">
              © 2026 AppEmbrace Inc. DBA Caveo. All rights reserved.
            </span>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden z-50 transition-transform duration-300 ${isScrolled ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-4386">
        <Link to="/intro-assessment" className="block w-full bg-[#8528e2] text-white py-3 rounded-lg font-bold text-center shadow-lg no-underline" data-id="element-4387">
          Get Your Free Security Checkup →
        </Link>
      </div>
    </div>;
}